import { useState } from "react";
import axios from "axios";
import { Field, Form, Formik } from "formik";
import { Typography, Grid2 } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import * as Yup from "yup";
import { useToaster } from "../../shared/utils/ToasterProvider";
import CustomDivider from "../../shared/utils/CustomDivider";
import TabsComponent from "../../shared/components/Tabs/TabComponent";
import TabPanel from "../../shared/components/Tabs/TabPanel";
import { NormalContainerWithHeader } from "../../shared/components/student/NormalComponent";
import { Constants, Grid_Gap, Input_Width_LG } from "../../shared/constants";
import { TextFormField } from "../../shared/components/formfields/TextFormField";
import { AutoCompleteField } from "../../shared/components/formfields/AutoCompleteField";
import EditableMarkdown from "../../shared/hook/EditableMarkdown";
import { useLmsDropdownOptions } from "../addlms/option/LsmOption";
import UtItem from "../../shared/utils/UtList";

const AddSyllabus = () => {

  const { showToaster } = useToaster();

  const [tabResponses, setTabResponses] = useState({});
  const [loadingTabs, setLoadingTabs] = useState({});
  const [selectedSub, setSelectedSub] = useState({})

  const [selectedGrade, setSelectedGrade] = useState({})
  const [selectedTag, setSelectedTag] = useState([])
  const { lmsGrade, lmsSubject, lmsTag } = useLmsDropdownOptions({ tagFilter: { status: true, subject_id: selectedSub?.id }, subjectFilter: { status: "active" }, gradeFilter: { status: true } })
  const validationSchema = Yup.object().shape({
    topic: Yup.string().required("Topic is required"),
    tags: Yup.string().required("Tags are required"),
    student_level: Yup.string().required("Student level is required"),
    teacher_efficacy: Yup.string().required("Teacher efficacy is required"),
    doc_name: Yup.string().required("Document name is required"),
  });

  const initialValues = {
    topic: "",
    tags: "",
    student_level: "",
    teacher_efficacy: "",
    doc_name: "",
  };

  const tabsData = [
    { label: "Content" },
    { label: "TLM" },
    { label: "Script" },
    { label: "Class quiz" },
    { label: "Worksheets" },
    { label: "Question Bank" },
    { label: "Student assessment" },
    { label: "Teacher assessment " }
  ];

  const studentLevelOption = [
    {
      id: 1,
      label: "Below average",
      value: "Below average"
    },
    {
      id: 2,
      label: "Average",
      value: "Average"
    },
    {
      id: 3,
      label: "Above average",
      value: "Above average"
    }
  ]

  const TeacherEfficacyOption = [
    {
      id: 1,
      label: "Easy",
      value: "Easy"
    },
    {
      id: 2,
      label: "Medium",
      value: "medium"
    },
    {
      id: 3,
      label: "Hard",
      value: "hard"
    }
  ]

  // ✅ Direct Axios POST with full URL
  // const mutationGenerate = useMutation({
  //   mutationFn: (formData) =>
  //     axios.post(`http://15.206.218.219:9000/gradesix/${selectedSub}/tlm`, formData, {
  //       timeout: 18000000,
  //     }),
  //   onSuccess: (res) => {
  //     setTabResponse(res.data);
  //     showToaster("Generated successfully!", "", "success");
  //   },
  //   onError: (err) => {
  //     showToaster(
  //       err?.response?.data?.error || "Failed to generate content",
  //       "",
  //       "error"
  //     );
  //   },
  // });



  const mutationGenerate = useMutation({
    mutationFn: ({ payload, tabLabel }) =>
      axios.post(
        `${Constants.AI_LMS_PATH}:${selectedGrade?.port}/${selectedGrade?.description}/${selectedSub?.value}/tlm`,
        payload,
        { timeout: 18000000 }
      ),

    onSuccess: (res, variables) => {
      const { tabLabel } = variables;


      setTabResponses((prev) => ({
        ...prev,
        [tabLabel]: res.data.generated_content,
      }));

      setLoadingTabs((prev) => ({
        ...prev,
        [tabLabel]: false,
      }));

      showToaster(`AiLms Generated successfully For ${tabLabel}!`, " ", "success");
      setSelectedTag([])
    },

    onError: (err, variables) => {
      const { tabLabel } = variables;

      setLoadingTabs((prev) => ({
        ...prev,
        [tabLabel]: false,
      }));

      showToaster(
        err?.response?.data?.error || "Failed to generate content",
        "",
        "error"
      );
    },
  });



  const [value, setValue] = useState(null);

  // const handleTabChange = (event, newValue, formValues) => {
  //   const { subject, ...restValue } = formValues
  //   setValue(newValue);

  //   const currentTab = tabsData[newValue];

  //   const payload = {
  //     ...restValue,
  //     format: currentTab.label,
  //   };

  //   mutationGenerate.mutate(payload);
  // };


  const handleTabChange = (event, newValue, formValues) => {
    const { subject, grade, tags, ...restValue } = formValues;
    setValue(newValue);

    const currentTab = tabsData[newValue];
    const tabLabel = currentTab.label;

    // 🔥 IMPORTANT LOGIC
    // If response already exists OR tab is loading → DO NOT CALL API AGAIN
    if (tabResponses[tabLabel] || loadingTabs[tabLabel]) {
      return;
    }

    const payload = {
      ...restValue,
      format: tabLabel,
      tags: selectedTag.map(item => item.tag).join(" ")
    };

    setLoadingTabs((prev) => ({
      ...prev,
      [tabLabel]: true,
    }));

    mutationGenerate.mutate({
      payload,
      tabLabel,
    });
  };
  const handleRemove = (id) => {
    setSelectedTag(prev => prev.filter(apartment => apartment.id !== id));
  };

  return (
    <NormalContainerWithHeader headerText="Syllabus">
      <Typography>Automatically generate syllabus section.</Typography>
      <CustomDivider />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          const { subject, ...restValue } = values;

          // Check: tab must be selected
          if (value === null || value === undefined) {
            showToaster("Please select a format before submitting.", "", "warning");
            return;
          }

          const currentTab = tabsData[value];

          if (!currentTab) {
            showToaster("Invalid tab selected.", "", "error");
            return;
          }

          const payload = {
            ...restValue,
            format: currentTab.label,
          };

          mutationGenerate.mutate(payload);
        }}

      >
        {({ values, setFieldValue }) => (
          <Form>
            <Grid2 container spacing={Grid_Gap}>
              <Grid2 children size={Input_Width_LG}>
                <Field
                  name="grade"
                  component={AutoCompleteField}
                  label="Select Grade"
                  options={lmsGrade.options ?? []}
                  onValueChange={(newValue) => { setSelectedGrade(newValue) }}
                  isRequired
                />
              </Grid2>

              <Grid2 children size={Input_Width_LG}>
                <Field
                  name="subject"
                  component={AutoCompleteField}
                  label="Select Subject"
                  options={lmsSubject.options ?? []}
                  onValueChange={(newValue) => {
                    setSelectedSub(newValue)
                    setFieldValue("tags", "")
                    setSelectedTag([])
                  }}
                  isRequired
                />
              </Grid2>
              <Grid2 children size={12}>
                {selectedTag?.map((item, index) => (
                  <UtItem key={index} text={item?.label} onRemove={() => { handleRemove(item?.id) }} />

                ))}
              </Grid2>

              <Grid2 children size={Input_Width_LG}>
                <Field name="topic" component={TextFormField} label="Topic" isRequired />
              </Grid2>

              <Grid2 children size={Input_Width_LG}>
                <Field
                  name="tags"
                  component={AutoCompleteField}
                  label="Select Tag"
                  options={lmsTag.options ?? []}
                  isRequired
                  onValueChange={(newValue) => {
                    setSelectedTag((prev) => {
                      const exists = prev.find((item) => item.id === newValue.id);
                      return exists ? prev : [...prev, newValue];
                    });
                    setFieldValue("tags", "")
                  }}
                />
              </Grid2>

              <Grid2 children size={Input_Width_LG}>
                <Field
                  name="student_level"
                  component={AutoCompleteField}
                  options={studentLevelOption ?? []}
                  label="Student Level"
                  isRequired
                />
              </Grid2>

              <Grid2 children size={Input_Width_LG}>
                <Field
                  name="teacher_efficacy"
                  component={AutoCompleteField}
                  options={TeacherEfficacyOption ?? []}
                  label="Teacher Efficacy"
                  isRequired
                />
              </Grid2>

              <Grid2 children size={Input_Width_LG}>
                <Field name="doc_name" component={TextFormField} label="Doc Name" isRequired />
              </Grid2>

              <Grid2 children size={12}>
                <TabsComponent
                  tabs={tabsData}
                  value={value}
                  onChange={(e, newVal) => handleTabChange(e, newVal, values)}
                />
                {/* <EditorComponent/> */}


                {tabsData.map((tab, index) => (
                  <TabPanel key={index} value={value} index={index}>
                    <Typography variant="subtitle1">
                      <strong>Format:</strong> {tab.label}
                    </Typography>

                    {/* <Typography variant="subtitle2" sx={{ mt: 1 }}>
                      <strong>API Response:</strong>
                    </Typography> */}

                    <pre
                      style={{
                        background: "#f8f8f8",
                        padding: 12,
                        borderRadius: 8,
                        fontSize: 13,
                        overflowX: "auto",
                        whiteSpace: "pre-wrap",
                        maxWidth: "100%",
                        paddingLeft: "3rem"
                      }}
                    >
                      {/* {mutationGenerate.isPending
                        ? "Generating..."
                        :
                        <>
                          <EditableMarkdown
                            content={tabResponse?.generated_content}
                            onSave={(updatedContent) => {
                              
                              saveSyllabusMutation.mutate({
                                content: updatedContent,
                                format: tab.label,
                                subject: selectedSub,
                              });
                            }}
                          />

                          <ReactMarkdown>{tabResponse?.generated_content}</ReactMarkdown>
                        </>
                      } */}

                      {loadingTabs[tab.label]
                        ? "Generating..."
                        : tabResponses[tab.label] ? (
                          <EditableMarkdown
                            content={tabResponses[tab.label]}
                            onSave={(updatedContent) => {
                              setTabResponses((prev) => ({
                                ...prev,
                                [tab.label]: updatedContent,
                              }));
                            }}
                          />
                        ) : (
                          "No content generated yet."
                        )
                      }



                    </pre>
                  </TabPanel>
                ))}
              </Grid2>
            </Grid2>
          </Form>
        )}
      </Formik>
    </NormalContainerWithHeader>
  );
};

export default AddSyllabus;


